import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { auth, db } from '@/integrations/firebase/client';
import { collection, query, where, onSnapshot, orderBy, Timestamp } from 'firebase/firestore';
import { ChartContainer, ChartConfig } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Calendar, CreditCard, Zap, TrendingUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ExecutionDoc {
  id: string;
  feedbackId: string;
  status: 'running' | 'done' | 'error';
  costs: {
    successCost: {
      credits: number;
    };
    errorCost: number;
    total: number;
  };
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

interface DailyUsage {
  day: string;
  credits: number;
}

interface UsageModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAddCreditsClick: () => void;
}

interface SummaryData {
  totalCredits: number;
  totalExecutions: number;
  peakDay: string;
  peakCredits: number;
}

// Helper to get the start and end of a month
const getMonthDateRange = (date: Date) => {
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);
  return { startOfMonth, endOfMonth };
};

export const UsageModal = ({ isOpen, onOpenChange, onAddCreditsClick }: UsageModalProps) => {
  const { t, i18n } = useTranslation();
  const [dailyData, setDailyData] = useState<DailyUsage[]>([]);
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [monthOptions, setMonthOptions] = useState<{label: string, value: string}[]>([]);

  useEffect(() => {
    // Generate month options for the last 24 months
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = date.toLocaleString(i18n.language, { month: 'long', year: 'numeric' });
      const value = date.toISOString();
      options.push({ label, value });
    }
    setMonthOptions(options);
  }, []);

  useEffect(() => {
    if (!auth.currentUser?.uid || !isOpen) return;

    const { startOfMonth, endOfMonth } = getMonthDateRange(selectedMonth);

    const executionsRef = collection(db, 'users', auth.currentUser.uid, 'executions');
    const q = query(
      executionsRef,
      where('createdAt', '>=', startOfMonth),
      where('createdAt', '<=', endOfMonth),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dailyMap = new Map<string, number>();
      let totalCredits = 0;
      const totalExecutions = snapshot.docs.length;

      snapshot.forEach((doc) => {
        const execution = { id: doc.id, ...doc.data() } as ExecutionDoc;
        if (execution.status === 'done' && execution.costs?.successCost?.credits) {
          const date = execution.createdAt.toDate();
          const day = date.getDate().toString();
          const credits = Number(execution.costs.successCost.credits) || 0;
          
          dailyMap.set(day, (dailyMap.get(day) || 0) + credits);
          totalCredits += credits;
        }
      });

      const daysInMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate();
      const chartData: DailyUsage[] = [];
      for (let i = 1; i <= daysInMonth; i++) {
        const day = i.toString();
        chartData.push({ day, credits: dailyMap.get(day) || 0 });
      }

      let peakDay = t('usage.notAvailable');
      let peakCredits = 0;
      if (dailyMap.size > 0) {
        const peakEntry = [...dailyMap.entries()].reduce((a, b) => b[1] > a[1] ? b : a);
        peakDay = peakEntry[0];
        peakCredits = peakEntry[1];
      }

      setDailyData(chartData);
      setSummaryData({ totalCredits, totalExecutions, peakDay, peakCredits });
    });

    return unsubscribe;
  }, [isOpen, selectedMonth]);

  const chartConfig = {
    credits: {
      label: t('usage.credits'),
      color: 'hsl(var(--primary))',
    },
  } satisfies ChartConfig;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <BarChart className="h-5 w-5" />
            {t('header.usage')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lg">{t('usage.monthlyUsage')}</CardTitle>
                  <CardDescription>{t('usage.monthlyUsageDesc')}</CardDescription>
                </div>
                <Select onValueChange={(value) => setSelectedMonth(new Date(value))} value={selectedMonth.toISOString()}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t('usage.selectMonth')} />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {summaryData && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t('usage.totalCreditsUsed')}</CardTitle>
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{summaryData.totalCredits.toFixed(1)}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t('usage.totalExecutions')}</CardTitle>
                      <Zap className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{summaryData.totalExecutions}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">{t('usage.peakDay')}</CardTitle>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{summaryData.peakDay}</div>
                      <p className="text-xs text-muted-foreground">{summaryData.peakCredits.toFixed(1)} {t('usage.credits')}</p>
                    </CardContent>
                  </Card>
                </div>
              )}
              {dailyData.length > 0 ? (
                <ChartContainer config={chartConfig} className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => [`${Number(value).toFixed(1)} ${t('usage.credits')}`, t('usage.credits')]} 
                      />
                      <Bar dataKey="credits" fill="var(--color-credits)" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  {t('usage.noData')}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end items-center pt-4">
            <Button onClick={() => onOpenChange(false)} variant="outline">
              {t('common.close')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};